import { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, BellOff, Loader2, Home, Check, X } from 'lucide-react';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CaregiverReminderBanner = ({ famigliaId, ignoreDismiss = false }) => {
  const [loading, setLoading] = useState(true);
  const [enabling, setEnabling] = useState(false);
  const [enableError, setEnableError] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isIOSBrowser, setIsIOSBrowser] = useState(false);
  const [storedSubscriptionId, setStoredSubscriptionId] = useState(null);
  const listenerRef = useRef(null);
  const hasRegisteredRef = useRef(false);

  const dismissKey = `caregiver_reminder_dismissed_${famigliaId}`;

  // Detect iOS and whether running as installed PWA
  useEffect(() => {
    const ua = navigator.userAgent;
    const isiOS = /iPad|iPhone|iPod/.test(ua);
    setIsIOS(isiOS);
    
    if (isiOS) {
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches 
        || window.navigator.standalone === true;
      setIsIOSBrowser(!isStandalone);
    }
    
    // Check if dismissed (7-day snooze)
    const dismissedTimestamp = localStorage.getItem(dismissKey);
    if (dismissedTimestamp) {
      const dismissedAt = parseInt(dismissedTimestamp, 10);
      const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
      const now = Date.now();
      if (now - dismissedAt < sevenDaysMs) {
        setDismissed(true);
      } else {
        // Snooze expired, clear it
        localStorage.removeItem(dismissKey);
      }
    }
  }, [dismissKey]);

  // Register subscription to backend
  const registerSubscription = useCallback(async (subscriptionId) => {
    if (!subscriptionId || !famigliaId || hasRegisteredRef.current) return false;
    
    try {
      console.log('[Caregiver] Registering subscription:', subscriptionId.substring(0, 20) + '...');
      const response = await fetch(`${API_URL}/api/notifications/register-caregiver`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          famiglia_id: famigliaId,
          subscription_id: subscriptionId,
          platform: isIOS ? 'ios' : 'web'
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.table_missing) {
          console.log('[Caregiver] Table not yet created');
          return false;
        }
        hasRegisteredRef.current = true;
        setStoredSubscriptionId(subscriptionId);
        setIsActive(true);
        return true;
      }
      return false;
    } catch (err) {
      console.error('[Caregiver] Register error:', err);
      return false;
    }
  }, [famigliaId, isIOS]);

  // Check backend status and sync if needed
  const checkStatus = useCallback(async () => {
    setLoading(true);
    hasRegisteredRef.current = false;
    
    try {
      if (!famigliaId) {
        setLoading(false);
        return;
      }

      // Get backend status
      const res = await fetch(`${API_URL}/api/notifications/caregiver-status/${famigliaId}`);
      if (!res.ok) {
        setLoading(false);
        return;
      }
      
      const data = await res.json();
      
      if (data.table_missing) {
        setLoading(false);
        return;
      }
      
      const backendIds = data.subscription_ids || [];

      // Check local OneSignal subscription
      let localOptedIn = false;
      let localSubscriptionId = null;
      
      try {
        if (window.OneSignal?.User?.PushSubscription) {
          localOptedIn = await window.OneSignal.User.PushSubscription.optedIn;
          localSubscriptionId = await window.OneSignal.User.PushSubscription.id;
        }
      } catch (e) {
        console.log('[Caregiver] OneSignal not available:', e.message);
      }

      // Check if local subscription is already registered
      const isRegistered = localSubscriptionId && backendIds.includes(localSubscriptionId);
      
      if (isRegistered) {
        setIsActive(true);
        setStoredSubscriptionId(localSubscriptionId);
      } else if (localOptedIn && localSubscriptionId && !backendIds.includes(localSubscriptionId)) {
        // Local is opted in but not registered - sync it
        console.log('[Caregiver] Syncing subscription to backend');
        await registerSubscription(localSubscriptionId);
      }

    } catch (err) {
      console.error('[Caregiver] Status error:', err);
    } finally {
      setLoading(false);
    }
  }, [famigliaId, registerSubscription]);

  // Setup subscription change listener
  useEffect(() => {
    const setupListener = async () => {
      try {
        if (!window.OneSignal?.User?.PushSubscription) return;

        const handleChange = async (event) => {
          console.log('[Caregiver] Subscription changed:', event);
          
          const optedIn = event?.current?.optedIn;
          const id = event?.current?.id;
          
          if (optedIn && id) {
            const success = await registerSubscription(id);
            if (success && enabling) {
              setEnabling(false);
            }
          }
        };

        listenerRef.current = handleChange;
        window.OneSignal.User.PushSubscription.addEventListener('change', handleChange);
        console.log('[Caregiver] Subscription listener registered');

      } catch (e) {
        console.log('[Caregiver] Could not setup listener:', e.message);
      }
    };

    setupListener();

    return () => {
      try {
        if (listenerRef.current && window.OneSignal?.User?.PushSubscription) {
          window.OneSignal.User.PushSubscription.removeEventListener('change', listenerRef.current);
        }
      } catch (e) {
        // Ignore cleanup errors
      }
    };
  }, [registerSubscription, enabling]);

  // Check status on mount
  useEffect(() => {
    checkStatus();
  }, [famigliaId, checkStatus]);

  const handleEnable = async () => {
    setEnabling(true);
    setEnableError(false);
    hasRegisteredRef.current = false;
    
    try {
      if (!window.OneSignal?.Notifications) {
        console.error('[Caregiver] OneSignal not available');
        setEnableError(true);
        setEnabling(false);
        return;
      }

      // Request permission
      await window.OneSignal.Notifications.requestPermission();
      
      const permission = await window.OneSignal.Notifications.permission;
      if (!permission) {
        console.log('[Caregiver] Permission not granted');
        setEnableError(true);
        setEnabling(false);
        return;
      }

      // Login with caregiver-specific external_id
      try {
        await window.OneSignal.login('caregiver:' + famigliaId);
        console.log('[Caregiver] Logged in with external_id: caregiver:' + famigliaId);
      } catch (loginErr) {
        console.log('[Caregiver] Login error (non-blocking):', loginErr);
      }

      // Opt in
      await window.OneSignal.User.PushSubscription.optIn();

      // Check if already subscribed
      const optedIn = await window.OneSignal.User.PushSubscription.optedIn;
      const subscriptionId = await window.OneSignal.User.PushSubscription.id;
      
      if (optedIn && subscriptionId) {
        const success = await registerSubscription(subscriptionId);
        if (!success) {
          setEnableError(true);
        }
      }
      // If not yet ready, the change event listener will handle it
      
    } catch (err) {
      console.error('[Caregiver] Enable error:', err);
      setEnableError(true);
    } finally {
      setEnabling(false);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem(dismissKey, Date.now().toString());
  };

  // Don't render if dismissed (unless ignoreDismiss), loading, or no famiglia
  if ((dismissed && !ignoreDismiss) || !famigliaId) {
    return null;
  }

  // Loading state - don't show banner
  if (loading) {
    return null;
  }

  // iOS browser (not installed) - show install message
  if (isIOSBrowser) {
    return (
      <div className="mx-4 mb-4 p-4 rounded-xl border bg-amber-50 border-amber-200">
        <div className="flex items-start gap-3">
          <Home className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-medium text-amber-800 text-sm">
              Promemoria terapie su iPhone
            </p>
            <p className="text-xs text-amber-600 mt-1">
              Funzionano solo dall'app installata. Tocca "Condividi" poi "Aggiungi a Home".
            </p>
          </div>
          <button onClick={handleDismiss} className="text-amber-400 hover:text-amber-600">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  // Already active - show small confirmation
  if (isActive) {
    return (
      <div className="mx-4 mb-4 p-3 rounded-xl bg-green-50 border border-green-200">
        <div className="flex items-center gap-2">
          <Check className="w-4 h-4 text-green-600" />
          <span className="text-sm text-green-700 font-medium">Promemoria terapie attivi</span>
        </div>
      </div>
    );
  }

  // Show activation banner
  return (
    <div className="mx-4 mb-4 p-4 rounded-xl border bg-blue-50 border-blue-200">
      <div className="flex items-start gap-3">
        <Bell className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <p className="font-medium text-blue-800 text-sm">
            Attiva promemoria terapie
          </p>
          <p className="text-xs text-blue-600 mt-1">
            Ricevi un avviso all'orario di ogni terapia
          </p>
          <Button 
            size="sm" 
            onClick={handleEnable}
            disabled={enabling}
            className="mt-3 bg-blue-600 hover:bg-blue-700 text-white"
          >
            {enabling ? (
              <>
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                Attivazione...
              </>
            ) : (
              'Attiva'
            )}
          </Button>
          {enableError && (
            <p className="text-xs text-red-600 mt-2">Attivazione non riuscita, riprova</p>
          )}
        </div>
        <button onClick={handleDismiss} className="text-blue-400 hover:text-blue-600">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default CaregiverReminderBanner;
